import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpService from "@shared/services/http.service";
import { API_CONFIG } from "@shared/constants/api";
import { notify } from "@/shared/tostNotification/notify";

export interface Curator {
    id: string;
    fullName: string;
    experienceYears: string | number;
    email: string;
    mobile: string;
    role: string;
    activeRequests: string | number;
    activeRequestsLabel: string;
    status: string;
    isAvailable: boolean;
    is_online: boolean;  
    experienceLabel: string;
    level: string;
    city: string;
    state: string;
    country: string;
    profileImage: string | null;
}

interface CuratorState {
    curators: Curator[];
    loading: boolean;
    error: string | null;
    page: number;
    hasMore: boolean;
    total: number;
}

const initialState: CuratorState = {
    curators: [],
    loading: false,
    error: null,
    page: 1,
    hasMore: true,
    total: 0,
};

export const fetchCurators = createAsyncThunk<
    { curators: Curator[]; total: number; page: number },
    { page: number; limit: number; search?: string },
    { rejectValue: string }
>(
    "curator/fetchCurators",
    async ({ page, limit, search }, { rejectWithValue }) => {
        try {
            const response = await httpService.get(API_CONFIG.path.assign_curator_list, {
                page,
                limit,
                searchString : search,
            });
            
            // Adjust based on actual API response structure
            const curatorList = Array.isArray(response) ? response : (response?.data ?? []);
            const total = response?.pagination?.total || response?.total || response?.totalCount || response?.count || curatorList.length;

            return {
                curators: curatorList,
                total,
                page,
            };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch curators");
        }
    }
);


export const assignCurator = createAsyncThunk<
    any,
    { requestId: string; curatorId: string },
    { rejectValue: string }
>(
    "curator/assignCurator",
    async ({ requestId, curatorId }, { rejectWithValue }) => {
        try {
            const response = await httpService.post(API_CONFIG.path.assign_curator, {
                requestId,
                curatorId,
                aiRequestId: requestId,
            });
            notify(response.message  || "Curator assigned successfully","success");
            return response.data;
        } catch (error: any) {
            notify(error.message || "Failed to assign curator","error");
            return rejectWithValue(error.message || "Failed to assign curator");
        }
    }
);

const curatorSlice = createSlice({
    name: "curator",
    initialState,
    reducers: {
        updateCuratorStatus: (
            state,
            action: PayloadAction<{ userId: string; isOnline: boolean }>
        ) => {
            const { userId, isOnline } = action.payload;
            const curator = state.curators.find((c) => c.id === userId);
            if (curator) {
                curator.is_online = isOnline;
            }
        },
        resetCuratorState: (state) => {
            state.curators = [];
            state.page = 1;
            state.hasMore = true;
            state.loading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCurators.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCurators.fulfilled, (state, action) => {
                state.loading = false;
                const { curators, total, page } = action.payload;
                
                if (page === 1) {
                    state.curators = curators;
                } else {
                    // Append only unique curators
                    const existingIds = new Set(state.curators.map((c) => c.id));
                    const newCurators = curators.filter((c) => !existingIds.has(c.id));
                    state.curators = [...state.curators, ...newCurators];
                }

                state.total = total;
                state.page = page;
                state.hasMore = state.curators.length < total;
            })
            .addCase(fetchCurators.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Assign Curator
            .addCase(assignCurator.pending, (state) => {
                state.loading = true;
            })
            .addCase(assignCurator.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(assignCurator.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});



export const { updateCuratorStatus, resetCuratorState } = curatorSlice.actions;
export default curatorSlice.reducer;
